import { POWERUP_KINDS, type PowerupKind } from '@/physics/powerups';

/**
 * howToPlayContent.ts — the PURE content behind the HOW TO PLAY screen (M86).
 * No DOM, no three.js: chapter list + the item field-guide live here so the
 * suite can enforce coverage (a new PowerupKind fails the test until the
 * guide teaches it — the guide can never silently fall behind the game).
 *
 * M86b copy rules (owner): descriptions read exciting, and NO em dashes in
 * any player-facing guide text (test-enforced).
 */

export type ChapterId = 'objective' | 'controls' | 'items' | 'skill';

export const CHAPTERS: readonly { id: ChapterId; label: string; kicker: string }[] = [
  { id: 'objective', label: 'OBJECTIVE', kicker: 'THE GOLDEN RULE' },
  { id: 'controls', label: 'CONTROLS', kicker: 'TAKE THE WHEEL' },
  { id: 'items', label: 'ITEMS', kicker: 'THE ARSENAL' },
  { id: 'skill', label: 'XP & SP', kicker: 'YOUR CAREER' },
];

/** Who tends to roll an item — mirrors tuning.powerups.weights in words. */
export type ItemWho = 'LEADERS' | 'TOP TWO' | 'FRONT-RUNNERS' | 'TRAILING' | 'LAST TWO';

/** M86b: the colour-coding language — every item belongs to one role. */
export type ItemCategory = 'speed' | 'control' | 'attack' | 'defense' | 'legend';

export const ITEM_CATEGORIES: Record<ItemCategory, { label: string; color: string }> = {
  speed: { label: 'SPEED', color: '#38e1ff' },
  control: { label: 'BALL CONTROL', color: '#6ef0b9' },
  attack: { label: 'ATTACK', color: '#ff7a3c' },
  defense: { label: 'DEFENSE', color: '#5aa2ff' },
  legend: { label: 'LEGEND', color: '#ffd34a' },
};

export interface ItemGuideEntry {
  kind: PowerupKind;
  who: ItemWho;
  cat: ItemCategory;
  /** One line, plain language — must fit the detail strip (see the test cap). */
  desc: string;
}

/** The field guide, in POWERUP_KINDS order (= the HUD roulette order). */
export const ITEM_GUIDE: readonly ItemGuideEntry[] = [
  {
    kind: 'boost',
    who: 'TRAILING',
    cat: 'speed',
    desc: 'Slam the throttle wide open. Pure speed, instant fire, gap closed in seconds.',
  },
  {
    kind: 'magnet',
    who: 'FRONT-RUNNERS',
    cat: 'control',
    desc: 'Snaps your ball onto your nose and holds it there. Get close, then reel it in.',
  },
  {
    kind: 'dozer',
    who: 'TRAILING',
    cat: 'attack',
    desc: 'Go full bulldozer. Anyone you touch gets launched aside, seeing stars.',
  },
  {
    kind: 'shield',
    who: 'LEADERS',
    cat: 'defense',
    desc: 'A force bubble that eats the next hit. Tackles, traps and spin-outs bounce off.',
  },
  {
    kind: 'kick',
    who: 'FRONT-RUNNERS',
    cat: 'control',
    desc: 'A cannon blast that sends your ball screaming down the course. Needs it close.',
  },
  {
    kind: 'tackle',
    who: 'TRAILING',
    cat: 'attack',
    desc: 'The one they all fear: a ghost car hunts the leader and slides them into the rail.',
  },
  {
    kind: 'wall',
    who: 'LEADERS',
    cat: 'defense',
    desc: 'Three glowing defenders rise behind you. Chasers bounce, balls rebound.',
  },
  {
    kind: 'offside',
    who: 'TRAILING',
    cat: 'attack',
    desc: 'The flag goes up and every racer ahead of you spins out. Shields block it.',
  },
  {
    kind: 'pibe',
    who: 'LAST TWO',
    cat: 'legend',
    desc: 'The golden star. Ball glued to your nose, boost speed, and nothing can stop you.',
  },
  {
    kind: 'studs',
    who: 'LEADERS',
    cat: 'attack',
    desc: 'Spikes burst from your wheels. Rivals spin out, their balls fly into the rails.',
  },
  {
    kind: 'scorpion',
    who: 'LAST TWO',
    cat: 'legend', // M86c owner call: the scorpion is a LEGEND-class play
    desc: 'Flip your ball sky-high, then volley it a monster distance. Untouchable in flight.',
  },
  {
    kind: 'mud',
    who: 'TOP TWO',
    cat: 'defense',
    desc: 'Dump a slick of mud behind you. Anyone who crosses it slips, slides and slows.',
  },
];

/** Guard used by the suite; exported so the screen can assert it in dev too. */
export function itemGuideCovers(kinds: readonly PowerupKind[] = POWERUP_KINDS): boolean {
  return ITEM_GUIDE.length === kinds.length && ITEM_GUIDE.every((e, i) => e.kind === kinds[i]);
}
